/**
 * Earthworks EW-6 invoices (fieldiq-api#1300): who may press what, the status
 * and kind badges, how the lines group, and the two decisions the screens draw
 * buttons from — how much of a quote is left to draw, and whether a job has
 * holdback to release.
 *
 * Every rule here is for drawing a button. The API decides, and a refusal it
 * still sends reads as a sentence through ewRefusal (lib/earthworks).
 */
import type { EwInvoice, EwInvoiceLine, EwHoldback, EwBillableTicket } from '../types/ew-invoices';
import { EW_ADMIN_ROLES, EW_UNKNOWN_SKIN, ewApiError, fmtMoney, type EwSkin } from './earthworks';

// ─── Who may press what (mirrors fieldiq-api EW_ADMIN_ROLES on every write) ──

/** Generate, edit a draft, mark sent, record a payment, void, release holdback. */
export const canWriteEwInvoices = (role: string | null | undefined): boolean =>
  !!role && (EW_ADMIN_ROLES as readonly string[]).includes(role);

// ─── Badges ──────────────────────────────────────────────────────────────────

/**
 * ew_invoices.status is exactly draft | sent | paid | void (fieldiq-api
 * EW_INVOICE_STATUS_KEYS). There is no partially-paid status: a part-paid
 * invoice is `sent` with a balance. An unknown value is `—` on slate.
 */
export const EW_INVOICE_STATUS_SKIN: Record<string, EwSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  sent: { label: 'Sent', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  paid: { label: 'Paid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ewInvoiceStatusSkin = (status: string | null | undefined): EwSkin =>
  status && Object.prototype.hasOwnProperty.call(EW_INVOICE_STATUS_SKIN, status) ? EW_INVOICE_STATUS_SKIN[status] : EW_UNKNOWN_SKIN;

/** The API's KIND_LABELS, for a list row that carries no label. */
export const EW_INVOICE_KIND_LABEL: Record<string, string> = {
  standard: 'Field tickets',
  from_quote: 'Quote',
  progress_draw: 'Progress draw',
  holdback_release: 'Holdback release',
  change_order: 'Change order',
};
/** The invoice's own kindLabel when it has one, else the map, else `—`. */
export const ewInvoiceKindText = (kind: string | null | undefined, kindLabel?: string | null): string =>
  kindLabel || (kind && EW_INVOICE_KIND_LABEL[kind]) || '—';

export const EW_PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'cheque', label: 'Cheque' },
  { value: 'eft', label: 'EFT' },
  { value: 'wire', label: 'Wire' },
  { value: 'credit_card', label: 'Credit card' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];
export const ewPaymentMethodText = (m: string | null | undefined): string =>
  (m && EW_PAYMENT_METHODS.find((x) => x.value === m)?.label) || m || '—';

// ─── Double billing: a warning the office confirms (Jason 2026-11-01) ─────────

/**
 * A generator's 409 billing_overlap: the job was already billed another way
 * (a quote draw, field tickets, a change order). Not a refusal — the API's
 * sentence is shown with "Confirm and invoice anyway", which resends the
 * same body with confirm_overlap: true. Null for any other error.
 */
export function ewBillingOverlapWarning(err: unknown): string | null {
  const e = ewApiError(err);
  if (e.code !== 'billing_overlap') return null;
  return e.message ?? 'This job has already been billed another way. Confirm to invoice it as well.';
}

/**
 * An approved change order the office may invoice directly: approved, with
 * lines on neither a live ticket nor a live invoice (the API's
 * unbilledLineCount). The API's change_order_already_billed still decides.
 */
export const ewCoCanInvoice = (co: { status?: string | null; unbilledLineCount?: number | null } | null | undefined): boolean =>
  !!co && co.status === 'approved' && typeof co.unbilledLineCount === 'number' && co.unbilledLineCount > 0;

// ─── What the record page offers, by status ──────────────────────────────────

export const ewInvoiceIsDraft = (s: string | null | undefined) => s === 'draft';
/** Mark sent: a draft only (the API's invoice_not_draft otherwise). */
export const ewInvoiceCanMarkSent = (s: string | null | undefined) => s === 'draft';
/** A payment: a sent invoice with a balance (draft → invoice_not_sent, paid → invoice_already_paid). */
export const ewInvoiceCanTakePayment = (inv: Pick<EwInvoice, 'status' | 'balanceDue'> | null | undefined) =>
  !!inv && inv.status === 'sent' && typeof inv.balanceDue === 'number' && inv.balanceDue > 0;
/** Void: draft or sent (paid → invoice_not_voidable; payments → invoice_has_payments, the API's call). */
export const ewInvoiceCanVoid = (s: string | null | undefined) => s === 'draft' || s === 'sent';

// ─── Lines ───────────────────────────────────────────────────────────────────

export interface EwLineGroup {
  key: string;
  /** Heading for the group: the ticket number, the quote, or the release. */
  title: string;
  lines: EwInvoiceLine[];
}

/**
 * The WORK lines, grouped in the order the API stored them: one group per
 * ticket, one for the quote's lines, one for a holdback release. The holdback
 * withheld and the deposit credit are NOT work lines — they are drawn in the
 * totals, in money order (ewInvoiceAdjustments).
 */
export function ewInvoiceLineGroups(
  inv: Pick<EwInvoice, 'lines' | 'quoteNumber' | 'quotePct' | 'holdbackLabel'> & { changeOrderNumber?: string | null },
): EwLineGroup[] {
  const groups: EwLineGroup[] = [];
  const byKey = new Map<string, EwLineGroup>();
  for (const l of [...inv.lines].sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (l.source === 'holdback' || l.source === 'deposit') continue;
    let key: string;
    let title: string;
    if (l.source === 'ticket') {
      key = `t:${l.ticketId ?? l.ticketNumber ?? ''}`;
      title = l.ticketNumber || 'Field ticket';
    } else if (l.source === 'quote') {
      key = 'quote';
      title = inv.quoteNumber
        ? `${inv.quoteNumber}${inv.quotePct !== null && inv.quotePct !== 100 ? ` · ${inv.quotePct}% draw` : ''}`
        : 'Quote';
    } else if (l.source === 'change_order') {
      key = 'change_order';
      title = inv.changeOrderNumber ? `Change order ${inv.changeOrderNumber}` : 'Change order';
    } else if (l.source === 'holdback_release') {
      key = 'release';
      title = `${inv.holdbackLabel || 'Holdback'} release`;
    } else {
      key = `other:${l.source}`;
      title = 'Other';
    }
    let g = byKey.get(key);
    if (!g) {
      g = { key, title, lines: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    g.lines.push(l);
  }
  return groups;
}

/** The holdback-withheld and deposit-credit lines, each a negative amount the API stored. */
export const ewInvoiceAdjustments = (inv: Pick<EwInvoice, 'lines'>) => ({
  holdback: inv.lines.filter((l) => l.source === 'holdback'),
  deposits: inv.lines.filter((l) => l.source === 'deposit'),
});

/** The tax rows actually levied: a label and amount the API stored. None → the caller shows the single Tax row. */
export function ewLeviedTaxRows(inv: EwInvoice): { label: string; percent: number | null; amount: number }[] {
  const rows: { label: string; percent: number | null; amount: number }[] = [];
  if (inv.taxRate1Label && typeof inv.taxRate1Amount === 'number') rows.push({ label: inv.taxRate1Label, percent: inv.taxRate1Percent, amount: inv.taxRate1Amount });
  if (inv.taxRate2Label && typeof inv.taxRate2Amount === 'number') rows.push({ label: inv.taxRate2Label, percent: inv.taxRate2Percent, amount: inv.taxRate2Amount });
  return rows;
}

// ─── Converting a quote: the draw % ──────────────────────────────────────────

/**
 * A draw % the Convert button will send: above 0, at most `remaining`, two
 * decimals at most. Returns the number, or a sentence saying what is wrong.
 */
export function ewDrawPctVerdict(input: string, remaining: number | null): { pct: number } | { error: string } {
  const t = input.trim();
  if (t === '') return { error: 'Enter the percentage of the quote to invoice.' };
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return { error: 'The percentage must be above 0.' };
  if (Math.round(n * 100) !== n * 100) return { error: 'Use at most two decimals.' };
  if (n > 100) return { error: 'The percentage cannot be more than 100.' };
  if (remaining !== null && Math.round(n * 100) > Math.round(remaining * 100)) {
    return { error: `At most ${remaining}% of this quote is left to invoice.` };
  }
  return { pct: n };
}

// ─── Holdback ────────────────────────────────────────────────────────────────

/** Release holdback: only when the API says something is outstanding and no release is live. */
export const ewHoldbackCanRelease = (h: Pick<EwHoldback, 'outstanding' | 'releaseInvoice'> | null | undefined): boolean =>
  !!h && typeof h.outstanding === 'number' && h.outstanding > 0 && !h.releaseInvoice;

// ─── Billable ────────────────────────────────────────────────────────────────

/**
 * Whether a selection of billable tickets may go on ONE invoice, mirrored from
 * the API's holdback_needs_one_job (holdback is tracked per job). Customer,
 * currency and province are already one per group. Null = fine.
 */
export function ewBillableSelectionProblem(tickets: EwBillableTicket[]): string | null {
  if (tickets.length === 0) return 'Pick at least one ticket.';
  const jobs = new Set(tickets.map((t) => t.jobId));
  if (jobs.size > 1 && tickets.some((t) => t.holdbackEnabled)) {
    return 'A job that takes holdback is invoiced on its own. Pick the tickets of one job.';
  }
  return null;
}

// ─── Pay now: a part payment ─────────────────────────────────────────────────

/**
 * The amount the customer's "Pay now" will send (EW-6b, POST
 * /invoice-pay/:token/checkout): above 0, two decimals at most, no more than
 * the balance owing. Returns the number, or a sentence saying what is wrong.
 * The API's INVALID_AMOUNT / AMOUNT_EXCEEDS_BALANCE still decide.
 */
export function ewPayAmountVerdict(input: string, balance: number | null): { amount: number } | { error: string } {
  const t = input.trim();
  if (t === '') return { error: 'Enter the amount to pay.' };
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return { error: 'The amount must be above 0.' };
  if (Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) return { error: 'Use at most two decimals.' };
  if (balance !== null && Math.round(n * 100) > Math.round(balance * 100)) {
    return { error: `The amount cannot be more than the ${fmtMoney(balance, null)} still owing.` };
  }
  return { amount: Math.round(n * 100) / 100 };
}
